import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {STUDIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/StudiesAnalyticsConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import preferences from 'Preferences';
import {StudiesPreferences} from 'studies/StudiesPreferences';
import usePreference from 'ui-library/hooks/usePreference';

/**
 * Gates the flowchart-based procedure run / run plan design UI.
 *
 * Use for imperative, call-time checks such as event handlers or non-React code. React render
 * paths should use useShowFlowchartProcedureRunDesign so they rerender when the preference changes.
 */
export function shouldShowFlowchartProcedureRunDesign(): boolean {
  return (
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN &&
    preferences.get(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN)
  );
}

/**
 * Reactively gates the flowchart-based procedure run / run plan design UI.
 *
 * Use in React components and custom hooks that need to rerender when the preference changes.
 */
export function useShowFlowchartProcedureRunDesign(): boolean {
  const [isFlowchartProcedureRunDesignEnabled] = usePreference<boolean>(
    StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN
  );
  return (
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN && isFlowchartProcedureRunDesignEnabled
  );
}

/**
 * Controls whether the flowchart opt-in banner should be shown in legacy run/run-plan design.
 */
export function shouldShowFlowchartProcedureRunDesignOptInBanner(): boolean {
  return (
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN &&
    !preferences.get(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN)
  );
}

/**
 * Opts the current user into the flowchart run/run-plan design.
 */
export async function optInToFlowchartProcedureRunDesign(actionOrigin: ActionOrigin): Promise<void> {
  await preferences.set(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN, true);
  trackEvent(STUDIES_ANALYTICS_ACTIONS.OPTED_IN_TO_FLOWCHART_PROCEDURE_RUN_DESIGN, {
    action_origin: actionOrigin,
  });
}

/**
 * Opts out of the flowchart run/run-plan design and selects a fallback method, since the legacy UI
 * requires a non-null selected method id.
 *
 */
export async function optOutOfFlowchartProcedureRunDesign(params: {
  actionOrigin: ActionOrigin;
  selectedMethodId: string | null;
  fallbackMethodId: string | undefined;
  setSelectedMethodId: (methodId: string) => void;
}): Promise<void> {
  const methodIdToSelect = params.selectedMethodId ?? params.fallbackMethodId;
  assert(
    methodIdToSelect != null,
    'Expected a fallback method id to be non-null before opting out of the flowchart design'
  );
  params.setSelectedMethodId(methodIdToSelect);
  await preferences.set(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN, false);
  trackEvent(STUDIES_ANALYTICS_ACTIONS.OPTED_OUT_OF_FLOWCHART_PROCEDURE_RUN_DESIGN, {
    action_origin: params.actionOrigin,
  });
}
